import { LOB } from "../../api/routes";
import * as client from "../../client";

/**
 * Centralized react-query key factory for the specialty-assistant data layer.
 *
 * Rules:
 *  - Define every key here; never write a key literal at a call site.
 *  - Put collection queries under `lists()` and anything belonging to a
 *    single report under `detail(reportId)`.
 *  - Sort array-valued key parts; never JSON.stringify a key part.
 *  - After patching a key with setQueryData, don't invalidate a prefix that
 *    covers it — use `invalidateReportDetail` in data.ts to exclude it.
 *
 * Rationale:
 *  - invalidateQueries matches by prefix, so the key hierarchy IS the
 *    invalidation API: `reportKeys.lists()` refreshes the dashboard without
 *    refetching open report details, and `reportKeys.detail(id)` drops a
 *    report plus everything derived from it in one call. A key defined
 *    outside this hierarchy silently opts out of both.
 *  - Invalidating a just-patched key starts a refetch that races with — and
 *    then overwrites — the patch.
 *  - react-query hashes arrays/objects in keys deterministically, so raw
 *    values work fine, stay readable in devtools, and stay matchable by
 *    `predicate` callbacks; JSON.stringify hides them. Sorting lets callers
 *    that pass the same set in different orders share one cache entry.
 */

export const reportKeys = {
  all: ["reports"] as const,
  lists: () => [...reportKeys.all, "list"] as const,
  list: (lobs: LOB[]) => [...reportKeys.lists(), [...lobs].sort()] as const,
  details: () => [...reportKeys.all, "detail"] as const,
  detail: (reportId: string) => [...reportKeys.details(), reportId] as const,
  calculatedPremium: (reportId: string) =>
    [...reportKeys.detail(reportId), "calculated-premium"] as const,
  precheck: (reportId: string) =>
    [...reportKeys.detail(reportId), "precheck"] as const,
  additionalFiles: (reportId: string) =>
    [...reportKeys.detail(reportId), "additional-files"] as const,
  emailThreads: (reportId: string) =>
    [...reportKeys.detail(reportId), "email-threads"] as const,
  clearanceRecipients: (reportId: string) =>
    [...reportKeys.detail(reportId), "clearance-recipients"] as const,
  // Chunked-summary prefixes: useChunkedAddressSummary appends a per-chunk
  // suffix to these. Invalidate via the prefix; never construct chunk keys
  // by hand.
  deductibleSummary: (reportId: string) =>
    [...reportKeys.detail(reportId), "deductible-summary"] as const,
  limitSummary: (reportId: string) =>
    [...reportKeys.detail(reportId), "limit-summary"] as const,
  gateExposure: (reportId: string, layerId: string) =>
    [...reportKeys.detail(reportId), "gate-exposure", layerId] as const,
  financialModelAudit: (
    reportId: string,
    source: string,
    batchId?: string | null
  ) =>
    [
      ...reportKeys.detail(reportId),
      "financial-model-audit",
      source,
      batchId ?? "active",
    ] as const,
  integrationData: (
    reportId: string,
    types: client.IntegrationDataRequest["types"]
  ) =>
    [
      ...reportKeys.detail(reportId),
      "integration-data",
      [...types].sort(),
    ] as const,
  // Per-layer Portfolio Marginal Impact at a participation share (percent).
  // The share is in the key, so a participation edit fetches fresh.
  layerPmi: (reportId: string, layerId: string, sharePct: number) =>
    [...reportKeys.detail(reportId), "layer-pmi", layerId, sharePct] as const,
};

// Standalone namespace for the cross-device staleness polls. Kept out of
// reportKeys/commentKeys so broad invalidations of those (e.g. reportKeys.all)
// don't re-trigger the polls, and so the polls only ever invalidate, never get
// invalidated. The dashboard poll is lob-independent (the server scopes to the
// user's accessible lobs), so it needs no key parameter.
export const freshnessKeys = {
  all: ["freshness"] as const,
  report: (reportId: string) =>
    [...freshnessKeys.all, "report", reportId] as const,
  reports: () => [...freshnessKeys.all, "reports"] as const,
};

// Summaries computed over the whole book, so editing any report can change them.
// Both reads here aggregate every bound/quoted deal — the per-layer one just also
// adds the viewed layer — so they live under one prefix that report mutations and
// the freshness polls invalidate wholesale (see invalidateBookSummaries).
export const bookKeys = {
  all: ["book"] as const,
  portfolioGateAccumulation: () =>
    [...bookKeys.all, "portfolio-gate-accumulation"] as const,
  aalPremiumGatePortfolio: () =>
    [...bookKeys.all, "aal-premium-gate-portfolio"] as const,
  aalPremiumGateTracking: (reportId: string, layerId: string) =>
    [...bookKeys.all, "aal-premium-gate-tracking", reportId, layerId] as const,
  gateTrackingBase: (reportId: string, layerId: string) =>
    [...bookKeys.all, "gate-tracking-base", reportId, layerId] as const,
};

export const commentKeys = {
  all: ["comments"] as const,
  // Dashboard batch fetch. Distinct "batch"/"report" discriminators keep a
  // stringified-ids entry from sharing a slot with a single report id.
  batch: (reportIds: string[]) =>
    [...commentKeys.all, "batch", [...reportIds].sort()] as const,
  forReport: (reportId: string) =>
    [...commentKeys.all, "report", reportId] as const,
  // All loss-run (per policy year) comments for a report, fetched once and
  // filtered to each year client-side.
  lossRunForReport: (reportId: string) =>
    [...commentKeys.all, "loss-run", reportId] as const,
  // All attachment comments for a report, fetched once and filtered to each
  // attachment client-side.
  attachmentForReport: (reportId: string) =>
    [...commentKeys.all, "attachment", reportId] as const,
};

export const policyFlyKeys = {
  all: ["policyfly-applications"] as const,
  list: (statuses: string) => [...policyFlyKeys.all, statuses] as const,
};

export const addressesForMapKeys = {
  all: ["addresses-for-map"] as const,
  list: (lobs: LOB[]) =>
    [...addressesForMapKeys.all, [...lobs].sort()] as const,
};

export const windTierKeys = {
  all: ["wind-tiers"] as const,
  // `joinedLocationKeys` is the sorted, comma-joined list of
  // windTierLocationKey values computed in useWindTiers.
  forLocations: (joinedLocationKeys: string) =>
    [...windTierKeys.all, joinedLocationKeys] as const,
};

export const googleGroupStatsKeys = {
  all: ["google-group-stats"] as const,
  forEmail: (email: string) => [...googleGroupStatsKeys.all, email] as const,
};

export const userKeys = {
  all: ["users"] as const,
};

export const crmContactKeys = {
  all: ["crm-contacts"] as const,
};

export const updatingReportKeys = {
  all: ["updating-reports"] as const,
};

export const jobKeys = {
  reportJobs: (reportId: string) => ["report-jobs", reportId] as const,
  allQueueJobs: ["queue-jobs"] as const,
  queueJobs: (reportIds: string[]) =>
    [...jobKeys.allQueueJobs, [...reportIds].sort()] as const,
};

export const policyApplicationKeys = {
  all: ["policy-applications"] as const,
  forReport: (reportId: string) =>
    [...policyApplicationKeys.all, "for-report", reportId] as const,
  fields: (applicationId: string) =>
    [...policyApplicationKeys.all, "fields", applicationId] as const,
  forms: (applicationId: string) =>
    [...policyApplicationKeys.all, "forms", applicationId] as const,
};
